import { useCallback, useRef, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import type { EditorView } from 'prosemirror-view'
import type { Schema } from 'prosemirror-model'
import { setBlockType } from 'prosemirror-commands'
import { TrailingIcon } from '@/assets/icons'
import { TextSizeLabel } from '../types'

interface TextSizeOption {
  label: TextSizeLabel
  nodeType: string
  level?: number
  className: string
}

const TEXT_SIZE_OPTIONS: TextSizeOption[] = [
  {
    label: TextSizeLabel.NORMAL_TEXT,
    nodeType: 'paragraph',
    className: 'text-size-normal',
  },
  {
    label: TextSizeLabel.HEADING_1,
    nodeType: 'heading',
    level: 1,
    className: 'text-size-h1',
  },
  {
    label: TextSizeLabel.HEADING_2,
    nodeType: 'heading',
    level: 2,
    className: 'text-size-h2',
  },
  {
    label: TextSizeLabel.HEADING_3,
    nodeType: 'heading',
    level: 3,
    className: 'text-size-h3',
  },
]

interface TextSizeDropdownProps {
  editorView: EditorView | null
  schema: Schema
  currentTextStyle: TextSizeLabel
}

export default function TextSizeDropdown({ editorView, schema, currentTextStyle }: TextSizeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const didApplyFormatRef = useRef(false)

  const applyTextSize = useCallback(
    (option: TextSizeOption) => {
      if (!editorView) return

      const nodeType = schema.nodes[option.nodeType]
      if (!nodeType) return

      const attrs = option.level ? { level: option.level } : undefined
      const command = setBlockType(nodeType, attrs)

      didApplyFormatRef.current = command(editorView.state, editorView.dispatch)
    },
    [editorView, schema]
  )

  if (!editorView) return null

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          className={`text-size-dropdown-trigger ${isOpen ? 'text-size-dropdown-trigger-open' : ''}`}
          aria-label={`Text size selector. Current size: ${currentTextStyle}`}
          aria-expanded={isOpen}
          aria-haspopup="true"
          type="button"
        >
          <span className="text-size-dropdown-label">{currentTextStyle}</span>
          <div className="text-size-dropdown-arrow-container" aria-hidden="true">
            <TrailingIcon className="text-size-dropdown-arrow" />
          </div>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="text-size-dropdown-content"
          data-preserve-editor-selection
          onCloseAutoFocus={(event) => {
            if (!didApplyFormatRef.current) return

            didApplyFormatRef.current = false
            event.preventDefault()
            // Return focus to the editor only after an option is applied; cancelled menus keep
            // Radix's normal focus behavior.
            editorView.focus()
          }}
          sideOffset={4}
          align="start"
        >
          {TEXT_SIZE_OPTIONS.map((option) => (
            <DropdownMenu.Item
              key={`${option.nodeType}-${option.level || 'default'}`}
              className="text-size-dropdown-item"
              onSelect={() => applyTextSize(option)}
            >
              <span className={option.className}>{option.label}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
